/**
 * The record fields a Directory pick may fill. For an add projection, the
 * text field whose box searches the Directory, the registry uuid field
 * the pick fills beside it, the one kind of entry the box lists, the
 * list's accessible name, and the sentence the dialog shows when the
 * server refuses the link (the entry was archived or removed between the
 * pick and the save). The text is always the saved value; the link
 * travels only beside it, and a keystroke after a pick drops it.
 */

import type { RecordAddProjection } from '@/lib/clientRecordEditApi';

export interface RecordDirectoryField {
  /** The registry uuid field the pick fills. */
  linkField: string;
  /** The one kind of Directory entry the box lists. */
  only: 'organization';
  /** The listbox's accessible name. */
  listLabel: string;
  /** The alert line when the server refuses the link. */
  refused: string;
}

type PerField = Readonly<Partial<Record<RecordAddProjection, Readonly<Record<string, RecordDirectoryField>>>>>;

export const RECORD_DIRECTORY_FIELDS: PerField = {
  'hospital-events': {
    facility: {
      linkField: 'facility_org_id',
      only: 'organization',
      listLabel: 'Hospital or rehab choices',
      refused: 'That Directory entry is no longer available. Pick it again, or keep the name as typed.',
    },
  },
};

/** The Directory field a text field is, or undefined for a plain box. */
export function directoryFieldOf(projection: RecordAddProjection, field: string): RecordDirectoryField | undefined {
  return RECORD_DIRECTORY_FIELDS[projection]?.[field];
}

/** The Directory field whose link field the server named in a refusal. */
export function directoryFieldByLink(
  projection: RecordAddProjection,
  linkField: string,
): RecordDirectoryField | undefined {
  return Object.values(RECORD_DIRECTORY_FIELDS[projection] ?? {}).find((d) => d.linkField === linkField);
}
